import { useState } from 'react'
import { Upload, FileText, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { trelloService } from '../lib/trello'
import { processFile, isSupportedFileType, getSupportedFileExtensions, getMaxFileSize } from '../utils/fileProcessor'

export default function UploadNotes() {
  const [notes, setNotes] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [processingStep, setProcessingStep] = useState<string>('')
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setError(null)
      
      // Validate file type
      if (!isSupportedFileType(selectedFile.name)) {
        setError('Unsupported file type. Please upload .txt, .md, or .pdf files.')
        return
      }
      
      // Validate file size
      if (selectedFile.size > getMaxFileSize()) {
        setError('File size exceeds 10MB limit. Please choose a smaller file.')
        return
      }
      
      setFile(selectedFile)
      setNotes('') // Clear text input when file is selected
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    const droppedFile = e.dataTransfer.files?.[0]
    if (droppedFile) {
      setFile(droppedFile)
      setNotes('') // Clear text input when file is dropped
    }
  }

  const readFileContent = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const content = e.target?.result as string
        resolve(content)
      }
      reader.onerror = reject
      reader.readAsText(file)
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!notes.trim() && !file) return

    setLoading(true)
    setError(null)
    setProcessingStep('')

    try {
      // Check Trello connection first
      if (!trelloService.isConnected()) {
        setError('Please connect your Trello account before processing notes. You can connect it in the Settings page.')
        setLoading(false)
        return
      }

      setProcessingStep('Checking authentication...')

      // Get current user id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      let content: string;

      if (file) {
        setProcessingStep('Processing file...')
        // Process file client-side to extract text
        const fileResult = await processFile(file);
        content = fileResult.text;
      } else {
        // Use provided text input
        content = notes.trim();
        if (!content) {
          throw new Error('No content to parse');
        }
      }

      setProcessingStep('Analyzing with AI...')
      // Send extracted text to Edge Function
      const { data, error } = await supabase.functions.invoke('parse-notes', {
        body: { 
          notes: content, 
          created_by: user.id 
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to parse notes');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      // Navigate to task review with the parsed tasks
      navigate('/dashboard/review', { 
        state: { 
          tasks: data.tasks,
          message: data.message 
        }
      });

    } catch (err) {
      console.error('Error parsing notes:', err)
      setError(err instanceof Error ? err.message : 'Failed to parse notes')
    } finally {
      setLoading(false)
      setProcessingStep('')
    }
  }

  const removeFile = () => {
    setFile(null)
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-text mb-4">Upload Meeting Notes</h1>
        <p className="text-gray-600">
          Upload a file or paste your meeting notes to extract actionable tasks
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Upload */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Upload File</h2>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-primary-500 bg-primary-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-2">
                  <FileText className="w-8 h-8 text-primary-500" />
                  <span className="font-medium">{file.name}</span>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="flex items-center space-x-2 text-red-600 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                  <span>Remove file</span>
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Drop your file here, or click to browse
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Supports: PDFs, text files (.txt, .md) (max 10MB)
                  </p>
                </div>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept={getSupportedFileExtensions()}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="btn-primary cursor-pointer"
                >
                  Choose File
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Text Input */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Or Paste Notes</h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Paste your meeting notes here...

Example:
Team meeting - John will create wireframes by Friday. Sarah needs to review the budget proposal. Mike will set up the development environment."
            className="w-full h-48 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={!!file}
          />
          {file && (
            <p className="text-sm text-gray-500 mt-2">
              Clear the uploaded file to use text input
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            type="submit"
            disabled={loading || (!notes.trim() && !file)}
            className="btn-primary text-lg px-8 py-4 disabled:opacity-50 disabled:cursor-not-allowed relative"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="loading-spinner"></div>
                <span>Analyzing with AI...</span>
              </div>
            ) : (
              'Parse with AI'
            )}
          </button>
          
          {loading && (
            <div className="mt-4 text-center">
              <div className="inline-flex items-center space-x-2 text-sm text-gray-600">
                <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                <span>{processingStep || 'Processing your meeting notes...'}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This may take a few moments for longer transcripts
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  )
} 